import React from 'react';
import axios from 'axios';
import { translate } from 'react-i18next';
import CBInputField from './form_components/CBInputField';
import CBButtonLink from './CBButtonLink';
import RadioButtonGroup from './RadioButtonGroup';
import { formatCandidateLabel, resolveCandidateChoice } from '../../utils/equipment';

/**
 * Find a customer's unregistered machine by its identifier, for the workshop
 * and chain "add to care service" dialogs.
 *
 * The fleet creates the machine; a workshop or chain only links it. The
 * backend searches only customers that already have a care-service deal with
 * the caller, and several of them may use the same identifier, so the result
 * may need a choice. The parent gets the chosen vehicle id (or null) through
 * `onSelect` and sends it with registration_country UNREGISTERED.
 *
 * Direct axios rather than a Redux action: the dialog needs the answer itself,
 * and the API middleware's .then() does not return the payload.
 */
class EquipmentCareServiceSearch extends React.Component {
  state = {
    identifier: '',
    searching: false,
    searched: false,
    failed: false,
    candidates: [],
    step: null,
    selectedVehicleId: null,
  };

  select = (vehicleId) => {
    const { onSelect } = this.props;
    this.setState({ selectedVehicleId: vehicleId });
    onSelect(vehicleId);
  };

  updateIdentifier = (changed) => {
    // A new search term invalidates whatever the last search found.
    this.setState({ identifier: changed.value, searched: false, candidates: [], step: null });
    this.select(null);
  };

  search = async () => {
    const { candidatesUrl, params } = this.props;
    const identifier = (this.state.identifier || '').trim();
    if (!identifier) return;

    this.setState({ searching: true, failed: false });
    try {
      const response = await axios.get(candidatesUrl, { params: { ...params, identifier } });
      const candidates = (response && response.data) || [];
      const choice = resolveCandidateChoice(candidates);
      this.setState({ searching: false, searched: true, candidates, step: choice.step });
      this.select(choice.vehicleId);
    } catch (error) {
      this.setState({ searching: false, searched: true, failed: true, candidates: [], step: null });
      this.select(null);
    }
  };

  renderResult = () => {
    const { t } = this.props;
    const { searched, failed, candidates, step, selectedVehicleId } = this.state;

    if (!searched) {
      return <p style={styles.hint}>{t('equipmentSearchHint')}</p>;
    }
    if (failed) {
      return <p style={styles.error}>{t('equipmentSearchFailed')}</p>;
    }
    if (step === 'none') {
      return <p style={styles.error}>{t('equipmentNotFoundInCareService')}</p>;
    }
    if (step === 'unavailable') {
      return <p style={styles.error}>{t('equipmentAllInCareService')}</p>;
    }
    if (step === 'single') {
      return (
        <p style={styles.found}>
          {`${t('equipmentFound')} ${formatCandidateLabel(candidates[0])}`}
        </p>
      );
    }

    const busy = candidates.filter((candidate) => candidate.already_in_care_service);
    return (
      <div>
        <RadioButtonGroup
          name='equipmentCandidate'
          title={t('chooseEquipmentHeading')}
          value={selectedVehicleId}
          setValue={this.select}
          options={candidates
            .filter((candidate) => !candidate.already_in_care_service)
            .map((candidate) => ({ value: candidate.vehicle_id, label: formatCandidateLabel(candidate) }))}
        />
        {
          busy.map((candidate) => (
            <p key={candidate.vehicle_id} style={styles.busy}>
              {`${formatCandidateLabel(candidate)} (${t('alreadyInCareServiceTag')})`}
            </p>
          ))
        }
      </div>
    );
  };

  render() {
    const { t, inputStyle, labelTextStyle } = this.props;
    const { identifier, searching } = this.state;

    return (
      <div style={styles.container}>
        <CBInputField
          name='equipmentIdentifier'
          labelText={t('equipmentIdentifierHeading')}
          labelTextStyle={labelTextStyle}
          inputStyle={inputStyle}
          value={identifier}
          placeholder={t('equipmentIdentifierPlaceholder')}
          onChange={this.updateIdentifier}
          enterAction={this.search}
        />
        <div style={styles.searchRow}>
          <CBButtonLink
            onClick={this.search}
            text={searching ? t('equipmentSearching') : t('equipmentSearchButton')}
          />
        </div>
        {this.renderResult()}
      </div>
    );
  }
}

const styles = {
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  searchRow: {
    marginTop: 4,
    marginBottom: 8,
  },
  hint: {
    fontSize: 13,
    color: '#666',
    textAlign: 'center',
  },
  found: {
    fontSize: 14,
    color: 'black',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  error: {
    fontSize: 13,
    color: '#c0392b',
    textAlign: 'center',
  },
  busy: {
    fontSize: 13,
    color: '#888',
    textAlign: 'center',
    margin: 0,
  },
};

export default translate('GarageView')(EquipmentCareServiceSearch);
